/**
 * A learner's own grade in one course — `GET /api/awards/v1/grades/me/<course_key>`.
 *
 * THREE SHAPES BEHIND ONE `grading` FIELD, and the shape is what decides what may be
 * shown, not a client-side guess from `percent` being present or absent:
 *
 *   graded          the course has graded work. `percent`, `pass_cutoff_percent` and the
 *                   per-assignment breakdown exist and may be shown.
 *   ungraded        the course has NO graded work. There is deliberately no `percent` and
 *                   no `pass_cutoff_percent` on the wire — the server does not compute a
 *                   number that would have no denominator, and a client must not invent
 *                   one either. Completion is the only fact this state has to report.
 *   misconfigured   the course's grading policy cannot reach 100% (a weight that does not
 *                   add up, graders with nothing behind them). The server still returns
 *                   `max_achievable_percent` and `pass_cutoff_percent` here, but they
 *                   describe a BROKEN POLICY, not this learner's standing in it — showing
 *                   them as "you scored" would blame a learner for the academy's setup.
 *
 * WHY THIS MATTERS ENOUGH TO TYPE AS A DISCRIMINATED UNION rather than one interface with
 * optional fields: an optional `percent: number | null` reads the same whether the course
 * has no graded work (ungraded) or has graded work this learner has not attempted yet
 * (graded, `percent: null`). Those are different facts — one says "nothing to grade here",
 * the other says "not yet graded" — and a single optional field cannot tell a caller which
 * one it is looking at. The union forces every renderer to check `grading` first, which is
 * the only way AC-050 (no percentage, ever, on an ungraded course) is enforceable at the
 * type level rather than by a component author remembering a rule.
 */

import { ApiError, openedx } from './openedx';

export type GradingMode = 'graded' | 'ungraded' | 'misconfigured';

/** How this course's certificate is decided. Never inferred — always the server's word. */
export type CertificateRule = 'pass' | 'completion' | 'withheld';

interface GradeCommon {
  api_version: string;
  course_key: string;
  certificate_rule: CertificateRule;
  complete: boolean;
  units_complete: number;
  units_incomplete: number;
  units_locked: number;
}

export interface UngradedGrade extends GradeCommon {
  grading: 'ungraded';
  message: string;
}

export interface MisconfiguredGrade extends GradeCommon {
  grading: 'misconfigured';
  message: string;
  /** A percentage. The ceiling the POLICY can reach — nobody in the course can beat it. */
  max_achievable_percent: number;
  /** A percentage. The pass mark the policy sets, unreachable or not. */
  pass_cutoff_percent: number;
}

export interface GradeAssignment {
  title: string;
  assignment_type: string;
  earned: number;
  possible: number;
  /** null when `possible` is 0 — a type with no graded content behind it yet. */
  percent: number | null;
  attempted: boolean;
  type_weight_percent: number | null;
}

export interface GradedGrade extends GradeCommon {
  grading: 'graded';
  /** A percentage, or null when this learner has not been graded on anything yet. */
  percent: number | null;
  pass_cutoff_percent: number | null;
  /**
   * Only meaningful when `grading === 'graded'`. On the other two shapes there is no
   * pass/fail question to have an answer to, so the field does not exist on them —
   * reading `.passed` off a narrower type is a compile error, not a silent `undefined`.
   */
  passed: boolean;
  letter_grade: string | null;
  /** A percentage. Null when not short of the pass mark (already passed, or no mark set). */
  shortfall_percent: number | null;
  assignments: GradeAssignment[];
}

export type MyGrade = UngradedGrade | MisconfiguredGrade | GradedGrade;

/** The learner's own grade in one course. Session-scoped — there is no other learner's to ask for. */
export const getMyGrade = (courseKey: string) =>
  openedx.get<MyGrade>(`/api/awards/v1/grades/me/${encodeURIComponent(courseKey)}`);

/** Where this page lives. A route in this app, keyed the same way `courseHref` is. */
export const gradesHref = (courseKey: string) => `/grades/${encodeURIComponent(courseKey)}`;

/**
 * Turn a failed read into something a learner can act on.
 *
 * TWO documented failures, and they mean opposite things to a learner:
 *
 *   503   a grading computation that could not complete. Transient — reloading is the
 *         right advice, and nothing has been lost.
 *   404   this academy does not serve that course to this learner. The endpoint refuses a
 *         course from another academy and a course the caller is not enrolled on with the
 *         SAME body, deliberately, so that neither answer confirms the other exists —
 *         `results_views.py`'s module docstring says why. Reloading will never fix it.
 *
 * 404 is reachable only by a typed or shared URL: `CourseHome` renders the link to this
 * page for an enrolled learner and nobody else. It still needs its own sentence, because
 * "could not be loaded" on a permanent refusal invites a learner to sit reloading a page
 * that will never load.
 *
 * The server's own `detail` is not echoed for a 404. It is written for whoever is holding
 * the key — "No such course for this academy" — and a learner who followed a stale link
 * needs to be told what to do instead. 401/403 are still the platform-wide session story
 * in `openedx.ts` rather than re-explained here.
 */
export function explainGradeFailure(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'The request never reached the academy. Check your connection, then try again.';
  }
  if (error.status === 404) {
    return 'This academy has no grades for you on that course. The link may be out of date, or you may not be enrolled on it — open the course from your dashboard to check.';
  }
  const detail = (error.body as { detail?: unknown } | null)?.detail;
  return typeof detail === 'string' ?
      detail
    : 'Your grade could not be loaded just now. Nothing has been lost — reload the page to try again.';
}
